import React, { useMemo, useState } from 'react';
import QuagganEmoji from './QuagganEmoji.jsx';
import { useWheelSettings } from '../context/WheelSettingsContext';

const normalizeName = (value) => String(value || '').trim().toLowerCase();

const buildNameRows = (names, clusterNames) => {
  if (!clusterNames) {
    return names.map((name, idx) => ({
      key: `row-${idx}-${normalizeName(name)}`,
      name,
      count: 1,
      indexes: [idx],
      firstIndex: idx,
    }));
  }

  const groupedByName = new Map();

  names.forEach((name, idx) => {
    const normalized = normalizeName(name);
    const existing = groupedByName.get(normalized);

    if (!existing) {
      groupedByName.set(normalized, {
        key: `cluster-${idx}-${normalized}`,
        name,
        count: 1,
        indexes: [idx],
        firstIndex: idx,
      });
      return;
    }

    existing.count += 1;
    existing.indexes.push(idx);
  });

  return Array.from(groupedByName.values());
};

function NamesPanel({
  names,
  setNames,
  getNameEmojiSeed,
  getNameIconSrc,
  onRerollNameEmoji,
  input,
  setInput,
  handleAddName,
  disabled,
}) {
  const { settings } = useWheelSettings();
  const [editingKey, setEditingKey] = useState(null);
  const [undoList, setUndoList] = useState(null);
  const [undoVisible, setUndoVisible] = useState(false);
  const clusterNames = Boolean(settings?.clusterNames);
  const showClusterCountControls = Boolean(settings?.showClusterCountControls);

  const nameRows = useMemo(
    () => buildNameRows(names, clusterNames),
    [names, clusterNames],
  );

  const shouldShowCountControls = (count) =>
    Boolean(clusterNames && showClusterCountControls && count > 1);

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text');
    if (!text) return;

    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    // If there's only a single non-empty line, fall back to normal paste behavior
    if (lines.length <= 1) {
      return;
    }

    // Prevent the raw text from being inserted into the input
    e.preventDefault();

    // Append all pasted names as separate entries
    setNames([...names, ...lines]);
    setInput("");
  };

  const handleRemoveName = (nameToRemove) => {
    const normalizedTarget = normalizeName(nameToRemove);
    setNames((previous) =>
      previous.filter((entry) => normalizeName(entry) !== normalizedTarget),
    );
  };

  const handleRenameRow = (row, nextName) => {
    const normalizedTarget = normalizeName(row.name);
    setNames((previous) =>
      previous.map((entry) =>
        normalizeName(entry) === normalizedTarget ? nextName : entry,
      ),
    );
    setEditingKey(null);
  };

  const handleSetRowCount = (row, nextCount) => {
    const targetCount = Math.max(1, Number(nextCount) || 1);
    const normalizedTarget = normalizeName(row.name);

    setNames((previous) => {
      const matchingIndexes = [];
      previous.forEach((entry, idx) => {
        if (normalizeName(entry) === normalizedTarget) {
          matchingIndexes.push(idx);
        }
      });

      const currentCount = matchingIndexes.length;
      if (currentCount === 0 || currentCount === targetCount) {
        return previous;
      }

      if (targetCount > currentCount) {
        return [...previous, ...Array.from({ length: targetCount - currentCount }, () => row.name)];
      }

      let removalsLeft = currentCount - targetCount;
      const next = [...previous];
      for (let idx = next.length - 1; idx >= 0 && removalsLeft > 0; idx -= 1) {
        if (normalizeName(next[idx]) !== normalizedTarget) continue;
        next.splice(idx, 1);
        removalsLeft -= 1;
      }
      return next;
    });
  };

  const handleClearOrUndo = () => {
    if (undoVisible && undoList) {
      setNames(undoList);
      setUndoList(null);
      setUndoVisible(false);
    } else if (names.length > 0) {
      setUndoList(names);
      setNames([]);
      setUndoVisible(true);
    }
  };

  // Reset undo state when names are changed (added/removed) except by undo
  React.useEffect(() => {
    if (!undoVisible) return;
    // If names changed and it's not the undoList, reset undo
    if (undoVisible && undoList && names.length > 0 && names !== undoList) {
      setUndoList(null);
      setUndoVisible(false);
    }
    // If names are changed to empty by other means, also reset
    if (undoVisible && names.length === 0 && !undoList) {
      setUndoVisible(false);
    }
  }, [names, undoList, undoVisible]);

  return (
    <div className="names-panel">
      <h2>Names</h2>
      <form onSubmit={handleAddName} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          value={input}
          onChange={e => !disabled && setInput(e.target.value)}
          onPaste={disabled ? undefined : handlePaste}
          placeholder="Enter a name"
          className="login-input"
          style={{ flex: 1 }}
          disabled={disabled}
        />
        <button type="submit" className="names-btn primary" disabled={disabled}>Add</button>
      </form>
      <div className="names-scroll-area">
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {nameRows.map((row) => (
            <NameItem
              key={row.key}
              name={row.name}
              count={row.count}
              disabled={disabled}
              isEditing={editingKey === row.key}
              onEdit={(newName) => handleRenameRow(row, newName)}
              onRemove={() => handleRemoveName(row.name)}
              onStartEdit={() => setEditingKey(row.key)}
              onCancel={() => setEditingKey(null)}
              showCountControls={shouldShowCountControls(row.count)}
              onIncreaseCount={() => handleSetRowCount(row, row.count + 1)}
              onDecreaseCount={() => handleSetRowCount(row, row.count - 1)}
              onSetCount={(nextCount) => handleSetRowCount(row, nextCount)}
              showQuagganEmojis={Boolean(settings?.showQuagganEmojis)}
              emojiSeed={getNameEmojiSeed ? getNameEmojiSeed(row.name, row.firstIndex) : `${row.name}-${row.firstIndex}`}
              nameIconSrc={getNameIconSrc ? getNameIconSrc(row.name, row.firstIndex) : ""}
              onRerollEmoji={() => onRerollNameEmoji?.(row.name, row.firstIndex)}
            />
          ))}
        </ul>
      </div>
      <div className="names-panel-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
        <button
          onClick={handleClearOrUndo}
          disabled={disabled || (!undoVisible && names.length === 0) || (undoVisible && !undoList)}
          className={`names-btn secondary ${disabled || (!undoVisible && names.length === 0) || (undoVisible && !undoList) ? 'disabled' : ''}`}
          style={{ display: (!undoVisible && names.length === 0 && !undoList) ? 'none' : 'inline-block' }}
        >
          {undoVisible ? 'Undo' : 'Clear All'}
        </button>
      </div>
    </div>
  );
}

function NameItem({
  name,
  count,
  isEditing,
  onEdit,
  onRemove,
  onStartEdit,
  onCancel,
  disabled,
  showCountControls,
  onIncreaseCount,
  onDecreaseCount,
  onSetCount,
  showQuagganEmojis,
  emojiSeed,
  nameIconSrc,
  onRerollEmoji,
}) {
  const [editValue, setEditValue] = useState(name);
  const [countInput, setCountInput] = useState(String(count || 1));
  const inputRef = React.useRef(null);
  const rowRef = React.useRef(null);
  // Reset edit value if name changes or edit mode toggles
  React.useEffect(() => {
    setEditValue(name);
  }, [name, isEditing]);

  React.useEffect(() => {
    setCountInput(String(count || 1));
  }, [count]);

  // Click-away detection
  React.useEffect(() => {
    if (!isEditing) return;
    function handleClick(e) {
      if (rowRef.current && !rowRef.current.contains(e.target)) {
        onCancel();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isEditing, onCancel]);

  const shouldShowQuagganEmoji = showQuagganEmojis && !nameIconSrc;
  const displayName = count > 1 && !showCountControls ? `${name} (${count})` : name;

  const EditIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="name-action-icon">
      <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25zm2.92 2.83H5v-.92l9.06-9.06.92.92L5.92 20.08zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
  );

  const RemoveIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="name-action-icon">
      <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.41L10.59 13.4 4.71 19.3 3.3 17.89 9.18 12 3.3 6.12 4.71 4.7l5.88 5.89 5.89-5.88z" />
    </svg>
  );

  const handleCountInputCommit = () => {
    const nextCount = Math.max(1, Number(countInput) || 1);
    onSetCount(nextCount);
  };

  return (
    <li
      ref={rowRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
        gap: 8,
        width: '100%',
        minWidth: 0,
      }}
    >
      {isEditing ? (
        <>
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="login-input"
            style={{ flex: 1, marginRight: 8 }}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (editValue.trim()) {
                  onEdit(editValue.trim());
                }
              }
            }}
            disabled={disabled}
          />
          <button
            className="names-btn primary"
            disabled={disabled}
            onClick={() => {
              if (editValue.trim()) {
                onEdit(editValue.trim());
              }
            }}
          >
            Save
          </button>
          <button className="names-btn secondary" disabled={disabled} onClick={onCancel} style={{ marginLeft: 4 }}>Cancel</button>
        </>
      ) : (
        <>
          <span
            className="name-item-text"
            style={{
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: 'none',
              paddingRight: 0,
            }}
          >
            {nameIconSrc ? (
              <img src={nameIconSrc} alt={`${name} icon`} className="name-item-custom-icon" />
            ) : shouldShowQuagganEmoji ? (
              <button
                type="button"
                className="name-item-emoji-btn"
                onClick={onRerollEmoji}
                disabled={disabled}
                title="Set a new random quaggan"
              >
                <QuagganEmoji
                  type="all"
                  seed={emojiSeed}
                  inline
                  size={22}
                  className="name-item-emoji"
                  style={{ marginRight: 8 }}
                />
              </button>
            ) : null}
            <span className="name-item-value">{displayName}</span>
          </span>
          <div
            className="name-item-actions"
            style={{
              position: 'static',
              transform: 'none',
              top: 'auto',
              right: 'auto',
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 4,
              flexWrap: 'nowrap',
              flex: '0 0 auto',
            }}
          >
            {showCountControls ? (
              <div
                className="name-count-controls"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '1px 2px',
                  borderRadius: 999,
                  background: 'rgba(4, 18, 38, 0.72)',
                  border: '1px solid rgba(80, 180, 255, 0.18)',
                }}
              >
                <button
                  type="button"
                  className="names-btn secondary"
                  onClick={onDecreaseCount}
                  disabled={disabled || count <= 1}
                  title="Decrease entries"
                  aria-label="Decrease entries"
                  style={{ width: 22, minWidth: 22, padding: 0, fontSize: 12, lineHeight: 1.2 }}
                >
                  −
                </button>
                <input
                  type="number"
                  min={1}
                  value={countInput}
                  onChange={(event) => setCountInput(event.target.value)}
                  onBlur={handleCountInputCommit}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    handleCountInputCommit();
                  }}
                  className="login-input"
                  style={{
                    width: 36,
                    minWidth: 36,
                    padding: '4px 3px',
                    textAlign: 'center',
                    marginBottom: 0,
                    fontSize: 13,
                  }}
                  disabled={disabled}
                  title="Set number of entries"
                />
                <button
                  type="button"
                  className="names-btn secondary"
                  onClick={onIncreaseCount}
                  disabled={disabled}
                  title="Increase entries"
                  aria-label="Increase entries"
                  style={{ width: 22, minWidth: 22, padding: 0, fontSize: 12, lineHeight: 1.2 }}
                >
                  +
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className="names-btn secondary icon-btn"
              onClick={onStartEdit}
              disabled={disabled}
              title="Edit name"
              aria-label="Edit name"
              style={{ width: 28, minWidth: 28, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <EditIcon />
            </button>
            <button
              type="button"
              className="names-btn secondary icon-btn"
              onClick={onRemove}
              disabled={disabled}
              title="Remove name"
              aria-label="Remove name"
              style={{ width: 28, minWidth: 28, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <RemoveIcon />
            </button>
          </div>
        </>
      )}
    </li>
  );
}

export default NamesPanel;
